"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, User, CircleAlert } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
function CampusBulletinsConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div data-section-id="57117" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => <div key={i} className="h-[420px] rounded-xl bg-[#d9d4cc]/20 animate-pulse" data-index={i} />)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <CircleAlert className="h-12 w-12 text-destructive mb-4" />
        <h3 className="text-lg font-bold text-[#3d2b1a] font-sans">Unable to load journal entries</h3>
        <p className="text-[#3d2b1a]/70 mb-4 font-sans">There was an error fetching the latest stories from Ubud.</p>
        <Button onClick={refetch} variant="outline" className="border-[#3d2b1a] text-[#3d2b1a] hover:bg-[#3d2b1a] hover:text-white">
          Try Again
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-12 bg-[#f5f2ed]/50 rounded-xl border border-[#d9d4cc] border-dashed">
        <p className="text-[#3d2b1a]/70 font-sans">No journal entries available at this time.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {posts.map((post, i) => {
      const category = post.categories && post.categories.length > 0 ? post.categories[0].name : "Journal";
      const date = post.date ? new Date(post.date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }) : "";
      return <Card key={post.id} data-index={i} className="group overflow-hidden border-[#d9d4cc] bg-white hover:border-[#3d2b1a]/30 transition-all duration-300 h-full flex flex-col shadow-none rounded-xl">
            {}
            <div className="relative aspect-[4/3] overflow-hidden bg-[#f5f2ed]">
              {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="h-full w-full flex items-center justify-center bg-[#5e6b4d]/10 text-[#5e6b4d]">
                  <span className="font-bold text-lg font-sans tracking-tight">Ubud Event Space</span>
                </div>}
              <div className="absolute top-4 left-4">
                <Badge variant="secondary" className="bg-[#f5f2ed]/90 text-[#3d2b1a] backdrop-blur-sm border-[#d9d4cc] hover:bg-white font-sans text-[10px] uppercase tracking-widest font-bold">
                  {category}
                </Badge>
              </div>
            </div>

            <CardHeader className="pb-2 pt-6">
              <div className="flex items-center gap-4 text-[11px] uppercase tracking-wider text-[#3d2b1a]/60 mb-2 font-sans font-semibold">
                {date && <div className="flex items-center gap-1.5">
                    <SafeHydrate><Calendar className="h-3.5 w-3.5" /></SafeHydrate>
                    <span>{date}</span>
                  </div>}
                {post.author && <div className="flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5" />
                    <span>{post.author.name}</span>
                  </div>}
              </div>
              <CardTitle className="text-xl font-bold leading-tight line-clamp-2 group-hover:text-[#5e6b4d] transition-colors font-sans text-[#3d2b1a]">
                <Link to={post.link || "#"}>
                  {post.title}
                </Link>
              </CardTitle>
            </CardHeader>

            <CardContent className="flex-grow">
              <CardDescription className="line-clamp-3 text-sm leading-relaxed text-[#3d2b1a]/70 font-sans">
                <div dangerouslySetInnerHTML={{
              __html: post.excerpt || ""
            }} />
              </CardDescription>
            </CardContent>

            <CardFooter className="pt-2 pb-6">
              <Link to={post.link || "#"} className="w-full">
                <div className="flex items-center text-xs font-bold uppercase tracking-widest text-[#3d2b1a] group/link">
                  Read More
                  <ArrowRight className="ml-2 h-4 w-4 text-[#5e6b4d] opacity-0 -translate-x-2 transition-all duration-300 group-hover/link:opacity-100 group-hover/link:translate-x-0" />
                </div>
              </Link>
            </CardFooter>
          </Card>;
    })}
    </div>;
}
export default function CampusBulletins() {
  const wp_query_params = {
    per_page: 3,
    orderby: "date",
    order: "desc",
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="stories-from-the-rice-fields" className="py-20 bg-[#f5f2ed] text-[#3d2b1a] relative overflow-hidden">
      {}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-[#3d2b1a]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-[#5e6b4d]/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6 border-b border-[#d9d4cc] pb-8">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-5xl font-bold text-[#3d2b1a] mb-4 tracking-tighter font-sans">
              Stories from the Rice Fields
            </h2>
            <p className="text-[#3d2b1a]/70 text-lg font-sans leading-relaxed">
              Discover event planning insights and stories from our bamboo sanctuary in the heart of Peliatan, Ubud.
            </p>
          </div>
          <div className="shrink-0">
            <Link to="/JournalPage">
              <Button variant="outline" className="group border-[#3d2b1a] text-[#3d2b1a] hover:bg-[#3d2b1a] hover:text-white rounded-full px-8 py-6 font-bold uppercase tracking-widest text-xs transition-all duration-300">
                Explore Our Journal
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>

        <PostsProvider wp_query={{
        per_page: 3,
        orderby: "date",
        order: "desc",
        status: ["publish"]
      }} className="w-full">
          <CampusBulletinsConsumer />
        </PostsProvider>
      </div>
    </section>;
}